import type {InputHTMLAttributes} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {useSearchInput} from './useSearchInput';

/**
 * The props for the `SearchInput` component.
 * Configures a styled search input matching the Figma design system.
 */
interface ComponentProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'SearchInput'
     */
    testId?: string;
}

/**
 * A styled search input with focus ring matching the Figma design system.
 * Renders with surfHigh background, border-radius 16, and a primaryDefault
 * focus border. Typography based on P body size.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'SearchInput'`.
 * @returns A JSX element rendering the SearchInput.
 *
 * @example
 * ```tsx
 * <SearchInput placeholder="Tour oder Stadtgebiet suchen…" value={query} onChange={handleChange} />
 * ```
 */
const SearchInput = ({testId = 'SearchInput', ...rest}: ComponentProps) => {
    const {handleBlur, handleFocus, isFocused} = useSearchInput();

    return (
        <Wrapper $isFocused={isFocused} data-cy={testId}>
            {/* ⚠️ Figma: 14px Regular — using body-inspired styling (15px), 1px größer */}
            <StyledInput
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                data-cy={`${testId}-input`}
                type="text"
                onBlur={handleBlur}
                onFocus={handleFocus}
            />
        </Wrapper>
    );
};

SearchInput.displayName = 'SearchInput';

export {SearchInput};

// ── Styled ──────────────────────────────────────────────────

interface WrapperFocusProps {
    /** Whether the input is currently focused. */
    $isFocused: boolean;
}

const Wrapper = styled.div<WrapperFocusProps>`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceHigh};
    border: ${({$isFocused, theme}) => (
        $isFocused
            ? `2px solid ${theme.colors.primaryDefault}`
            : '2px solid transparent'
    )};
    border-radius: 1.6rem;
    display: flex;
    height: 5.2rem;
    padding: 0 ${spacing(5)};
    transition: border-color 0.2s ease;
`;

const StyledInput = styled.input`
    background: transparent;
    border: none;
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.5;
    margin: 0;
    outline: none;
    padding: 0;
    width: 100%;

    &::placeholder {
        color: ${({theme}) => theme.colors.inkDisabled};
    }
`;